import axios from 'axios';
import { updateTimezone as updateTimezoneRoute } from '@/routes/user';
import type { AuthUser } from '@/types/models';

function detectBrowserTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
        return '';
    }
}

/**
 * Persist the browser's IANA timezone for a signed-in user who doesn't have one
 * yet. Registration (OAuth + email code) never sets `timezone`, so without this
 * a user stays NULL until they happen to open Settings. Calling this globally
 * from app.ts backfills every authenticated user on their next page load and
 * captures new signups.
 *
 * The `PUT /user/timezone` route lives in the dashboard-domain group, so this is
 * gated to the app subdomain — a no-op on landing or for guests. Fire-and-forget:
 * a failure just leaves the timezone unset for another attempt on the next load.
 */
export function captureTimezoneIfMissing(user: AuthUser | null | undefined): void {
    if (!user || user.timezone) {
        return;
    }
    if (!window.location.hostname.startsWith('app.')) {
        return;
    }

    const timezone = detectBrowserTimezone();
    if (!timezone) {
        return;
    }

    axios
        .put(updateTimezoneRoute.url(), { timezone })
        .then(() => {
            user.timezone = timezone;
        })
        .catch(() => {
            // Non-critical: next page load retries.
        });
}
